const DB_NAME = "KorimyDB";
const DB_VERSION = 2; // 버전 2로 업그레이드 (필요 시 증가)

// 데이터 타입 정의
interface StoredData {
  id: string | number;
  data: any;
}

// 버전별 테이블 정의
const TABLES_BY_VERSION: Record<number, string[]> = {
  1: ["settings", "users", "chats", "notes", "games"],
  2: ["test"], // 임시 테이블 추가.
};

// 모든 테이블 목록 (캐싱용)
const ALL_TABLES = Array.from(new Set(Object.values(TABLES_BY_VERSION).flat()));

// 데이터베이스 연결 캐싱
let dbPromise: Promise<IDBDatabase> | null = null;

const openDB = (): Promise<IDBDatabase> => {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      console.info(
        `IndexedDB upgrading to ${DB_VERSION} from ${event.oldVersion}`,
      );
      const db = request.result;

      // 버전별로 필요한 테이블 생성
      for (
        let version = event.oldVersion + 1;
        version <= DB_VERSION;
        version++
      ) {
        const tables = TABLES_BY_VERSION[version] || [];
        tables.forEach((table) => {
          if (!db.objectStoreNames.contains(table)) {
            db.createObjectStore(table, { keyPath: "id" });
          }
        });
      }
    };
  });

  return dbPromise;
};

// 데이터 저장
export const setData = async (
  table: string,
  id: string | number,
  data: any,
): Promise<void> => {
  const db = await openDB();

  if (!ALL_TABLES.includes(table)) {
    throw new Error(`Object store '${table}' does not exist.`);
  }

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([table], "readwrite");
    const store = transaction.objectStore(table);

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);

    store.put({ id, data });
  });
};

// 데이터 조회
export const getData = async (
  table: string,
  id: string | number,
): Promise<StoredData | null> => {
  const db = await openDB();

  if (!ALL_TABLES.includes(table)) return null;

  return new Promise((resolve) => {
    const transaction = db.transaction([table], "readonly");
    const store = transaction.objectStore(table);
    const request = store.get(id);

    request.onsuccess = () => {
      const result = request.result;
      if (!result) {
        resolve(null);
        return;
      }

      // data가 문자열인지 확인하고, 문자열이면 JSON.parse로 변환
      let parsedData: any;
      if (typeof result.data === "string") {
        try {
          parsedData = JSON.parse(result.data);
        } catch (e) {
          console.warn(`Failed to parse data for ${id} in ${table}:`, e);
          parsedData = result.data; // 파싱 실패 시 원래 문자열 반환
        }
      } else {
        parsedData = result.data;
      }

      resolve({
        id: result.id,
        data: parsedData,
      });
    };
    request.onerror = () => resolve(null);
  });
};

// 데이터 삭제
export const deleteData = async (
  table: string,
  id: string | number,
): Promise<void> => {
  const db = await openDB();

  if (!ALL_TABLES.includes(table)) return;

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([table], "readwrite");
    const store = transaction.objectStore(table);

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);

    store.delete(id);
  });
};

// 채팅방 목록 캐싱 함수들

// 사용자별 고유 키 생성
const getChatsKey = (userId: string) => `__CHATS_${userId}__`;

export const getCachedChatsFromIDB = async (userId: string) => {
  if (!userId) {
    console.warn("getCachedChatsFromIDB: userId is null");
    return null;
  }
  const result = await getData("chats", getChatsKey(userId));
  return result?.data || null;
};
export const saveCachedChatsToIDB = async (userId: string, chats: any) => {
  if (!userId) {
    console.warn("saveCachedChatsToIDB: userId is null");
    return;
  }
  await setData("chats", getChatsKey(userId), chats);
};

// 사용 예시
//@ts-ignore
async function example() {
  try {
    // 데이터 저장
    await setData("test", "user1", { theme: "dark" });
    // 데이터 조회
    const result = await getData("test", "user1");
    console.log(result); // { id: "user1", data: { theme: "dark" } }
    // 데이터 삭제
    await deleteData("test", "user1");
  } catch (error) {
    console.error("Error:", error);
  }
}
